/**
 * Shapes the console renders. These MIRROR the backend
 * (`functions/src/System/Migration/steps.ts`) — they do not define anything.
 *
 * The step list itself, and its ORDER, are NOT duplicated here: `GetRun` returns
 * `steps`, and the page renders whatever the callable says. That is the point of
 * "la página es un cliente más" — there is exactly one definition of the runbook,
 * it lives server-side, and a console built against a stale copy of the order is
 * not a failure mode we can have on go-live day.
 */

// ⚠️ EL `id` NO ES EL NÚMERO (2026-07-24). Es una clave semántica y estable
// (`gate--c`, `copy-legacy--c`); el número que se ve sale de la posición y llega
// como `label`. Comparar un `step.id` contra un NÚMERO es, desde hoy, un bug:
// compara contra un id que ya no existe y el bloque no se pinta NUNCA — que es
// como se perdió el «por qué está gris» del paso de los árboles. La suite de
// backend vigila este fichero y los dos `.vue`, y falla si aparece uno.
//
// `pre` = prerrequisito que se verifica antes de la ventana. Un solo botón:
// "confirmo que esto está". Sin cronómetro y sin nota — no se ejecuta nada, así que
// no hay nada que medir. `manual` SÍ los lleva, y ahí sí importan: los pasos 3
// (backup), 4 (snapshot) y 5 (import) existen para clavar esos números.
export type MigrationStepKind = "auto" | "review" | "manual" | "pre";

/** `pre` = prerrequisito (se numera `P-1…`), `run` = la ventana (`1…N`). */
export type MigrationStepPhase = "pre" | "run";

export type MigrationStepStatus =
  | "pending"
  | "running"
  | "done"
  | "failed"
  | "skipped";

export interface MigrationStepDef {
  /**
   * CLAVE SEMÁNTICA Y ESTABLE (`gate--c`, `flip-front--a`), NO el número de
   * pantalla. Es la clave de `run.steps[…]` y la que viaja a las callables.
   * NUNCA se pinta: para pintar está `label`.
   */
  id: string;
  phase: MigrationStepPhase;
  /**
   * EL NÚMERO QUE SE VE — `"P-1"`, `"14"`. Lo calcula el BACKEND desde la
   * posición del paso (`migrationStepsWithLabels`), no esta página: deducirlo
   * aquí serían dos reglas que tienen que coincidir, y esta consola existe
   * precisamente para que el runbook tenga UNA definición.
   */
  label: string;
  kind: MigrationStepKind;
  title: string;
  detail: string;
  command?: string;
  callable?: string;
  requires?: string[];
  warning?: string;
  /**
   * SÓLO MIRA: el paso no escribe NADA — comprueba que un fichero está, saca un
   * informe, da un veredicto. AUSENTE = escribe algo, aunque sea un campo.
   * NO es el dry-run: aquél es un MODO de un paso que sí escribe.
   */
  readOnly?: boolean;
  /**
   * Qué NO se puede ensayar contra el emulador. AUSENTE = el paso corre entero,
   * que es la mayoría. Existe para que el operador no tenga que preguntar si le
   * toca hacer algo o si ahí, sencillamente, no aplica.
   */
  emulator?: MigrationEmulatorGap;
  /**
   * LOS FORMULARIOS QUE HAY QUE RELLENAR A MANO EN OTRA CONSOLA. Hoy: las diez
   * instancias de la extensión de BigQuery, con sus 25 campos cada una.
   *
   * AUSENTE en casi todos los pasos, y la consola pinta las pestañas sólo cuando
   * está: no hay id de paso escrito en el front, así que el día que otro paso
   * declare `instances` le salen solas.
   */
  instances?: MigrationStepInstance[];
}

/**
 * UN CAMPO DEL FORMULARIO DE OTRA CONSOLA, tal y como se ve en ella.
 *
 * `label` viaja EN INGLÉS y literal —«Cloud Functions location»— porque el
 * operador lo está comparando contra la pantalla de Firebase, campo por campo.
 * Traducirlo aquí le obligaría a traducir de vuelta a las 3am.
 *
 * `value: ""` es un valor, no un hueco: hay campos que van deliberadamente
 * vacíos. La consola los pinta como «(vacío)» para que se vea que están
 * decididos y no que se olvidaron.
 */
export interface MigrationInstanceParam {
  label: string;
  value: string;
}

/** Una instancia a crear: su id y sus campos, EN EL ORDEN DEL FORMULARIO. */
export interface MigrationStepInstance {
  id: string;
  /**
   * QUÉ LE TOCA A ÉSTA: `"NUEVA"`, `"ACTUALIZAR"` o `"RENOMBRAR desde <id>"`.
   *
   * No son diez instalaciones iguales: tres se crean de cero y siete ya existen
   * en prod — y de esas siete, dos además CAMBIAN DE NOMBRE, que es desinstalar
   * y reinstalar, no pulsar «actualizar». Sin esto las diez pestañas se ven
   * idénticas y la que hay que desinstalar no se distingue de la que sólo hay
   * que revisar.
   *
   * Opcional en el tipo para que un `instances` sin él siga compilando.
   */
  action?: string;
  /**
   * Los campos del formulario. ⚠️ PUEDE VENIR VACÍO y es legítimo: una instancia
   * `NO SE TOCA` no tiene formulario que rellenar. Y el número NO es fijo — las
   * de BigQuery traen 25, la del correo es otra extensión con otros campos.
   */
  params: MigrationInstanceParam[];
  /**
   * UNA LÍNEA sobre esta instancia, cuando la tabla de campos no basta: qué
   * cambia de verdad en un `ACTUALIZAR`, o por qué una `NO SE TOCA` está aquí.
   * Si viene, píntala; es lo único que llevan las que no tienen campos.
   */
  note?: string;
}

/** Los servicios que el emulador no tiene. */
export type MigrationCloudTool =
  | "gcloud"
  | "bq"
  | "gsutil"
  | "firebase-deploy"
  | "extensions";

export interface MigrationEmulatorGap {
  /** `"no"`: no corre nada. `"partial"`: corre una parte. */
  runs: "no" | "partial";
  tools: MigrationCloudTool[];
  /** La parte que SÍ corre, o qué la sustituye en el ensayo. */
  inEmulator?: string;
  /** La parte que NO, y qué pasa si la corres igual. */
  notInEmulator: string;
}

export interface MigrationStepState {
  id: string;
  status: MigrationStepStatus;
  startedAt?: number;
  completedAt?: number;
  durationMs?: number;
  by?: string;
  note?: string;
  result?: unknown;
}

export interface MigrationRun {
  runId: string;
  /** THE migration instant, decided once when the run opens. See the backend. */
  migrationDate: number;
  frozenAt?: number;
  frozenBy?: string;
  environment: string;
  createdAt: number;
  createdBy: string;
  status: "open" | "completed" | "aborted";
  steps: Record<string, MigrationStepState>;
  cmmiWhitelistActiveBefore?: boolean | null;
  /**
   * BD de escritura del run (#595). Fijada al abrir el run, inmutable después.
   * Ausente / vacía => (default): el go-live real. Un `prueba-N` aísla el grafo
   * de negocio del ensayo. Espejo del campo homónimo en el backend (steps.ts).
   */
  writeDbId?: string;
}

/**
 * The freeze, as the console sees it. ONE boolean, read from the ONE flag that
 * actually gates the callables (`systemConfiguration/frontend.offline`, #560's
 * FreezeGuard). The console does not own a second freeze doc — a banner that
 * reads a different document from the guard is a banner that lies.
 *
 * Who froze it and when live on the RUN (`migrationRuns/{runId}`), which is the
 * audit trail, not the gate.
 */
export interface MigrationFreezeState {
  frozen: boolean;
}

// -- the paso-14 draft ------------------------------------------------------

export interface MigrationDraftNode {
  username: string;
  displayUsername: string;
  tier: string;
  counterTier: "full" | "lite";
  package: string;
  isAnchor: boolean;
  isGenesis: boolean;
  place: string;
  enroller: string;
  unilevelParentPK: string;
  unilevelSide: string;
  /**
   * NULL cuando el nodo NO entra en el binario (`place: "unilevel"`) — no ocupa
   * pierna y es transparente al derrame. El backend lo declara así
   * (`DraftNode.binaryParentPK`) y aquí decía `string` a secas: quien lo leyera
   * sin comprobarlo reventaba en cuanto el corte incluyera uno de ésos.
   */
  binaryParentPK: string | null;
  binarySide: string;
  rankId: string;
  rankPriority: number;
  startDate: number | null;
  order: number;
}

export interface MigrationDraft {
  generatedFor: string;
  root: string;
  migrationDate: number;
  nodes: MigrationDraftNode[];
  unplaceable: Array<{ username: string; reason: string }>;
  warnings: string[];
}

// -- el draft del CMMI (paso `build-cmmi--c`) --------------------------------
//
// MIRROR de `functions/src/Subscriptions/Activations/migration/mmiTypes.ts`.
// Estaba declarado a mano dentro de `MmiDraftPreview.vue` con la mitad de los
// campos; vive aquí desde que hay más de un componente que lo lee.
//
// ES UN ÁRBOL DE UN SOLO VÍNCULO: no hay izquierda ni derecha, cada uno cuelga
// de su `parentPK` y ya está. Lo que sí tiene y los otros dos no es el PASE
// (`isPassUp`), que es justo lo que no se entiende en una tabla.

export interface MigrationMmiDraftNode {
  username: string;
  /** De quién cuelga en el árbol nuevo. El ápice para los génesis. */
  parentPK: string;
  isGenesis: boolean;
  /**
   * EL PASE. `true` = esta persona NO cuelga de quien la patrocinó, sino de
   * alguien más arriba, porque su patrocinador no calificaba para recibirla.
   * El apply lo consume TAL CUAL (D11): nunca lo recalcula.
   */
  isPassUp: boolean;
  /** Cuál de las listas del padre llenó el replay. `null` en los génesis. */
  listId: "prelaunch" | "start" | "cicle" | null;
  /** El instante de compra del CMMI — la clave del orden de replay (D10). */
  cmmiDateMs: number;
  dateSource: string;
  /** Dónde colgaba en el CMMI VIEJO. `null` = no tenía nodo legacy. */
  legacyParent: string | null;
  legacyIsPassUp: boolean | null;
  order: number;
}

export interface MigrationMmiDraft {
  generatedFor: string;
  chapter: string;
  root: string;
  migrationDate: number;
  nodes: MigrationMmiDraftNode[];
  unplaceable: Array<{ username: string; reason: string }>;
  warnings: string[];
}

// -- el reporte de corte del paso 23 (#579) ---------------------------------
//
// MIRROR de `functions/src/System/Migration/pointsReport.ts`. Sólo los campos que
// la consola pinta: el JSON completo se enseña tal cual, así que este tipo no
// tiene que ser exhaustivo — pero lo que SÍ nombra tiene que llamarse igual que
// allá, o la tabla sale vacía sin avisar.
//
// El reporte era del paso 6 y RESOLVÍA el árbol para poder decir `inverted`.
// Ahora va después del 12 y sólo LEE: los puntos y el lado viejo de legacy-2026,
// el lado nuevo del árbol ya construido en la BD del run.
//
// CÓMO SE PINTA: primero interna/derrame (la lectura operativa — la etiqueta
// viaja con la persona y no se rompe con una inversión), y sólo después
// izquierda/derecha como dato de apoyo para cuadrar.
//
// EL RANGO NO SE PINTA, y tampoco se calcula ya en el backend (2026-07-26). En el
// reinicio los puntos de binario se TRASLADAN pero el rango RECALIFICA DESDE
// CERO, así que enseñar volumen de rango en la pantalla desde la que se decide
// restituir invita a restituirlo. Este tipo tenía `rank` y `totals.rank*`; se
// fueron con él.

export interface PointsReportLeg {
  side: "left" | "right";
  points: number;
  paid: number;
  label: "derrame" | "interna" | "unknown";
}

/** Una pierna SIN transformar, tal y como está guardada en su base. */
export interface PointsReportRawLeg {
  pending: number;
  paid: number;
  lost?: number;
}

export interface PointsReportRow {
  usernamePK: string;
  /** true cuando el lado del nodo se INVIRTIÓ en el reset (piernas cambiadas). */
  inverted: boolean;
  /** false => colocado pero SIN documento en legacy-2026: los ceros son reales. */
  legacyPresent: boolean;
  legacyBinarySide: "left" | "right" | null;
  resolvedBinarySide: "left" | "right" | null;
  derrameSide: "left" | "right" | null;
  internaSide: "left" | "right" | null;
  binary: { left: PointsReportLeg; right: PointsReportLeg; total: number };
  /**
   * HOY: `binaryTree/{u}.points` de la base del run, SIN transformar.
   *
   * `binary` de arriba son los puntos de ANTES traídos al marco nuevo (cruzados
   * si el nodo cambió de pierna). Éste es lo que hay escrito AHORA MISMO en el
   * árbol nuevo, que entre el paso de los árboles y la restitución es cero para
   * todos — y después de aplicar el import es lo que se acaba de escribir. Por
   * eso la tabla enseña las dos: la de antes dice cuánto habría que devolver, y
   * ésta si ya está devuelto.
   *
   * OPCIONAL a propósito: un backend anterior a este campo no lo manda, y la
   * columna tiene que poder decir «no lo sé» en vez de un cero que se lee como
   * «no se ha restituido».
   */
  now?: { left: PointsReportRawLeg; right: PointsReportRawLeg };
  /**
   * ANTES: `legacy-2026/binary-tree/{u}.points`, TAL CUAL, sin cruzar.
   *
   * NO es lo mismo que `binary` — y la diferencia es justo la de las filas que
   * importan. `binary` son estos mismos puntos ya traídos a las piernas de HOY
   * (intercambiados si la cuenta cambió de pierna), que es la lectura con la que
   * se opera. Éste es el documento del snapshot, literal, y es lo único contra lo
   * que se puede CUADRAR: si la cuenta invirtió, `binary.left` NO coincide con lo
   * que hay escrito en el snapshot, y comprobar un número contra él daría un
   * susto falso.
   *
   * Opcional por lo mismo que `now`: un backend anterior no lo manda y la columna
   * tiene que poder decir «no lo sé».
   */
  before?: { left: PointsReportRawLeg; right: PointsReportRawLeg };
  /**
   * Una de las cuentas de PIERNA INFINITA. NO bloquea la fila: la AVISA. La
   * migración le crea piernas nuevas, así que aplicar el import encima
   * probablemente duplica lo que el propio proceso genera — pero la decisión es
   * renglón por renglón y la toma el operador, de modo que la fila conserva sus
   * botones y lleva la advertencia al lado. El único renglón sin botones es el
   * ápice. Opcional: un backend anterior a este campo no lo manda.
   */
  expirationExempt?: boolean;
  /**
   * 🔴 LO QUE EL NODO DECLARA DE MÁS: `binary-tree/{u}.points.{pierna}.pending`
   * del árbol VIEJO menos la suma de los registros vivos de esa pierna. Viene en
   * el marco del árbol viejo (se lee con `legacyBinarySide`).
   *
   * POSITIVO = el nodo dice tener puntos que ningún documento respalda, y eso
   * bloquea el import: el import copia DOCUMENTOS, así que sin ellos escribiría
   * cero y dejaría la fila marcada como hecha. Es el caso de `carlosdiamante`
   * (400 declarados, ni un registro detrás).
   *
   * NEGATIVO = hay más detalle que nodo, y NO bloquea: los registros existen,
   * viajan con su fecha y el import corrige el descuadre al recalcular.
   *
   * Opcional: un backend anterior a este campo no lo manda.
   */
  nodeMinusDetail?: { left: number; right: number };
  /**
   * 🔴 EL CONSOLIDADO — la suma de los `pending` de los REGISTROS VIVOS que
   * aterrizarían en cada pierna del árbol NUEVO. Es exactamente lo que el import
   * escribiría al pulsar «Aplicar», y por tanto lo único que hay respaldado por
   * documentos con fecha.
   *
   * VIENE YA EN EL MARCO NUEVO — el backend lo calcula en destino, cruzado si la
   * cuenta volteó —, así que se lee con `derrameSide`/`internaSide` y NUNCA con
   * `legacyBinarySide`.
   *
   * Es la cifra con la que nace la caja de «crear puntos» (Hector, 2026-07-26):
   * «el correcto debe de ser el consolidado, porque son los que están
   * respaldados por algo». El otro número —lo que declara el nodo del árbol
   * viejo— sale de `binary`, y los dos se enseñan juntos en la fila para que la
   * decisión se tome viéndolos, sin abrir el pop-up del descuadre.
   *
   * Opcional por lo mismo que `now` y `before`: un backend anterior no lo manda.
   */
  willBe?: { left: number; right: number };
}

/**
 * EL ESTADO DEL IMPORT DE UNA FILA, sin la fila. Es lo que hace falta para que
 * los dos botones salgan ya apagados cuando no hay nada que hacer.
 *
 * Sale del documento de bitácora que escribe el importador
 * (`migrationRuns/{runId}/binaryPointsImports/{pk}`), NO de lo que esta pantalla
 * recuerde haber pulsado: al recargar la consola, o al refrescar el reporte, lo
 * que la pantalla recordaba se pierde y lo que está escrito sigue estando.
 */
/**
 * LOS TRES ESTADOS DE LA BITÁCORA DE UN IMPORT. Es el mismo `ImportStatus` del
 * backend (`functions/src/System/Migration/binaryPointsImport.ts`), escrito aquí
 * porque entre los dos lados no viaja el tipo, sólo el JSON.
 *
 * `applying` es el que hay que entender: las cuentas grandes no caben en un solo
 * commit de Firestore, así que la copia se hace en lotes y deja de ser atómica.
 * El importador escribe la bitácora ENTERA —con la lista de ids— antes de copiar
 * el primer documento, y sólo la marca `applied` cuando termina. Una bitácora
 * parada en `applying` significa que el intento se cortó a la mitad: hay
 * documentos escritos, no se sabe cuántos, y lo que toca es BORRAR.
 */
export type ImportRowStatus = "applying" | "applied" | "undone";

export interface ImportRowSummary {
  usernamePK: string;
  status: ImportRowStatus;
  /** ¿Se intercambiaron las piernas al aplicar? */
  crossed: boolean;
  /** `padre.lado` de cuando se aplicó. */
  treeFingerprint: string;
  /** `padre.lado` de ahora mismo. */
  currentFingerprint: string;
  /**
   * Hay un import APLICADO y la huella ya no coincide: alguien rehizo los
   * árboles debajo. La fila no vale — hay que borrar y volver a aplicar. Por eso
   * «Borrar» sigue habilitado en este caso: es el camino de vuelta.
   */
  staleFingerprint: boolean;
  totals: { records: number; pending: number };
}

/**
 * Lo que devuelve `Migration-ImportBinaryPoints` — LA FILA RELEÍDA DE FIRESTORE
 * después de aplicar o borrar, más su bitácora. No es lo que el servidor creía
 * que iba a pasar: es lo que hay escrito.
 */
export interface ImportRowState {
  row: PointsReportRow;
  audit: {
    status: ImportRowStatus;
    crossed: boolean;
    treeFingerprint: string;
    written: { left: string[]; right: string[] };
    totals: { records: number; pending: number };
  } | null;
  /**
   * Hay un import aplicado y la huella del árbol YA NO COINCIDE: alguien rehizo
   * los árboles debajo. La fila no vale — hay que borrar y volver a aplicar.
   */
  staleFingerprint: boolean;
  currentFingerprint: string;
}

export interface PointsReport {
  generatedAt: number;
  rows: PointsReportRow[];
  /**
   * Colocados sin documento en `legacy-2026` — un ancla sembrada a mano que no
   * viene del snapshot. YA NO ES EL CASO DE `gtr.master` (2026-07-23): el clon
   * del paso 9 le copia `users/gtr.academy` también en `legacy-2026`, así que
   * ahí SÍ tiene documento. Se queda como red de seguridad para cualquier otra:
   * una cuenta colocada que el reporte se comiera en silencio es justo el fallo
   * que esto viene a cazar.
   */
  placedWithoutLegacy: string[];
  /** Docs de `binaryTree` sin `binarySide` — el backlink del ápice, no miembros. */
  nodesWithoutSide: string[];
  /** El cuadre resumen-vs-detalle de las CUATRO piernas de cada cuenta. */
  cuadre?: CuadreReport;
  totals: {
    accounts: number;
    inverted: number;
    binaryPending: number;
  };
}

// ---------------------------------------------------------------------------
// EL CUADRE: ¿dice el resumen lo mismo que sus documentos?
// ---------------------------------------------------------------------------
//
// La tabla enseña el RESUMEN del nodo (`points.{pierna}.pending`), y ese número
// puede contradecir a los documentos de detalle que hay debajo. Las dos
// direcciones son casos reales y piden acciones OPUESTAS:
//
//   resumen > detalle   el nodo declara puntos que no existen (el fallo de
//                       `carlosdiamante`: 400 declarados, cero documentos).
//   detalle > resumen   hay registros vivos que el resumen no cuenta, y que por
//                       tanto no ve nadie.
//
// SÓLO `pending` CUADRA. `paid` y `lost` viajan para poder verlos enfrentados,
// pero no los mantiene nadie contra el detalle — medido el 2026-07-26: 22 de las
// 54 piernas del árbol viejo «descuadran» en `lost` sin que nada esté mal.

export type CuadreTree = "viejo" | "nuevo";

export interface CuadreFigures {
  pending: number;
  paid: number;
  lost: number;
}

export interface CuadreLeg {
  usernamePK: string;
  /** `viejo` = `legacy-2026` (la fuente) · `nuevo` = la base del run. */
  tree: CuadreTree;
  /** El nombre de la base, para el rótulo. */
  dbId: string;
  leg: "left" | "right";
  /** derrame/interna EN EL MARCO DE ESE ÁRBOL. */
  label: "derrame" | "interna" | "unknown";
  /** Lo que declara el nodo — la cifra que enseña la tabla. */
  resumen: CuadreFigures;
  /** Lo que suman los registros VIVOS de la pierna. */
  consolidado: CuadreFigures;
  /** El `binaryPoints-summary` de la pierna, cuando el documento existe. */
  summaryDoc: CuadreFigures | null;
  /** `resumen.pending - consolidado.pending`. Nunca 0. */
  diff: number;
  direction: "resumen-de-mas" | "detalle-de-mas";
  liveRecords: number;
  deadRecords: number;
  /** Los primeros ids vivos: con ellos el diagnóstico se ve de un vistazo. */
  ids: string[];
  idsTotal: number;
}

export interface CuadreReport {
  ok: boolean;
  /** 4 por cuenta: dos árboles × dos piernas. */
  checkedLegs: number;
  mismatches: CuadreLeg[];
  keys: { viejo: string[]; nuevo: string[] };
}

/**
 * El estado de la FOTO FIRMADA del paso — lo que decide si la pantalla está
 * bloqueada. Ver `functions/src/System/Migration/reportSnapshot.ts`.
 */
export interface ReportConfirmation {
  confirmed: boolean;
  confirmedAt?: number;
  confirmedBy?: string;
  /** Qué número de validación es (1 la primera). */
  attempt?: number;
  /** Lo que ocupa la foto guardada. */
  sizeBytes?: number;
  /** Los descuadres del árbol VIEJO que se reconocieron al firmar. */
  ackViejo?: string[];
  cancelCount?: number;
  cancelledAt?: number;
}

/**
 * Lo que devuelve `Migration-PointsReport`: el reporte Y el estado del import de
 * cada cuenta. Van JUNTOS en la misma llamada a propósito — si los botones de la
 * tabla se pintaran de una segunda lectura, habría un instante en que la tabla ya
 * está y los botones todavía no saben, y ese instante se resuelve pulsando.
 */
export interface PointsReportPayload {
  report: PointsReport;
  /** Una entrada por cuenta CON bitácora. Las demás no se han tocado nunca. */
  imports?: ImportRowSummary[];
  /** ¿Está la foto firmada? Si lo está, la pantalla va bloqueada. */
  confirmation?: ReportConfirmation;
}

// -- the paso-16 gate -------------------------------------------------------

export interface InvariantResult {
  id: string;
  title: string;
  status: "pass" | "fail" | "pending";
  violations: string[];
  /** Cuántas hubo DE VERDAD, antes de truncar a la muestra. */
  violationCount?: number;
  /** `violations` es una muestra, no la lista entera. */
  truncated?: boolean;
  pendingReason?: string;
}

/**
 * EL INFORME DE UN GATE — sirve para los DOS.
 *
 * El del paso 18 (estructural) y el de descongelar tienen la misma forma en todo
 * lo que la pantalla pinta —`ok`, el resumen y la lista de resultados— y se
 * diferencian sólo en el pie: el primero dice CUÁNTO CARGÓ (`loaded`) y el
 * segundo CONTRA QUÉ BUCKET miró (`storage`). Por eso los dos campos son
 * opcionales y el componente pinta el que venga: un tipo por gate obligaría a
 * duplicar el componente entero para cambiar una línea de pie.
 */
export interface GateReport {
  ok: boolean;
  ranAt: number;
  environment: string;
  /** Sólo el gate del paso 18. */
  loaded?: {
    users: number;
    unilevelNodes: number;
    binaryNodes: number;
    binaryIndexDocs: number;
    elapsedMs: number;
  };
  /** Sólo el gate de descongelar. */
  storage?: { bucket: string | null; unavailable?: string };
  summary: { passed: number; failed: number; pending: number };
  report: {
    results: InvariantResult[];
    passed: InvariantResult[];
    failed: InvariantResult[];
    pending: InvariantResult[];
    ok: boolean;
  };
}

/**
 * UNA FILA DEL LISTADO DE INVARIANTES, ya resuelta para pintar.
 *
 * `estado` NO es el `status` del backend: lleva un valor más que el informe no
 * tiene y la pantalla sí necesita — `porCorrer`, o sea que el gate todavía no ha
 * contestado por esta invariante. Es el estado con el que nacen TODAS al pulsar
 * «Ejecutar», y es lo que distingue «no se sabe» de «pasó».
 */
export type InvariantRowState = "porCorrer" | "pass" | "fail" | "pending";

export interface InvariantRow {
  id: string;
  title: string;
  estado: InvariantRowState;
  /** El resultado, cuando ya hay informe. */
  result?: InvariantResult;
  /**
   * Llegó en el informe pero NO está en el índice. Se pinta igual, al final:
   * ocultar un resultado del gate es mucho peor que enseñar una fila de más.
   * Hoy le pasa a `GATE-LOAD`, que el motor añade sólo cuando salta.
   */
  fueraDelIndice?: boolean;
}

/**
 * `ListRuns` — the console's landing call. Carries the freeze because the banner
 * has to be able to tell the truth with NO run open; see the callable.
 */
export interface ListRunsPayload {
  runs: MigrationRun[];
  freeze: MigrationFreezeState;
}

export interface GetRunPayload {
  run?: MigrationRun;
  environment: string;
  freeze: MigrationFreezeState;
  steps: MigrationStepDef[];
  drafts: {
    tree?: { json: string; appliedAt?: number; resolvedBy?: string };
    mmi?: { json: string; appliedAt?: number; resolvedBy?: string };
  };
}

/**
 * Whether a step may run. MIRRORS the backend's `stepIsUnlocked` — but this is
 * only a HINT that greys a button out. The callable re-checks it server-side and
 * refuses, because a disabled button protects nothing.
 */
export function stepIsUnlocked(
  step: MigrationStepDef,
  run: MigrationRun | undefined
): boolean {
  if (!step.requires?.length) return true;
  return step.requires.every((req) => {
    const s = run?.steps?.[req]?.status;
    return s === "done" || s === "skipped";
  });
}
